import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock, Tractor } from "lucide-react";
import { SEO } from "../components/SEO";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { getCity, CITIES } from "../data/cities";
import { SERVICES } from "../data/services";
import NotFound from "./NotFound";

const CityDetail = () => {
  const { citySlug } = useParams();
  const city = getCity(citySlug);
  if (!city) return <NotFound />;
  const nearby = CITIES.filter((c) => c.slug !== city.slug && c.county.includes(city.county.split(" ")[0])).slice(0, 5);

  return (
    <main data-testid="city-detail-page">
      <SEO
        title={`Mobile Welding Repair in ${city.name}, TX | Aledo Welding`}
        description={`On-site welding repair in ${city.name}, TX — gates, trailers, equipment, railings. Steel, aluminum & stainless. On-site ${city.response}. Text a photo for a fast quote.`}
      />
      <PageHero
        kicker={`${city.county} County · ${city.tag}`}
        title={`Mobile welding repair in ${city.name}, TX`}
        lede={city.blurb}
        testidPrefix="city"
      />
      <section className="bg-paper pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="flex items-center gap-3 bg-cream border border-warmline p-5 mb-12 max-w-xl" data-testid="city-response">
              <Clock size={22} className="text-rust shrink-0" aria-hidden />
              <p className="text-[15px] text-smoke"><strong className="text-ink">Response time:</strong> on-site {city.response} for most {city.name} calls. Same-day repairs available.</p>
            </div>
          </Reveal>
          {city.rural && (
            <Reveal>
              <div className="flex items-start gap-3 bg-sand border border-warmline p-5 mb-12 max-w-2xl" data-testid="city-rural-note">
                <Tractor size={22} className="text-steel shrink-0 mt-0.5" aria-hidden />
                <p className="text-[15px] text-smoke">
                  <strong className="text-ink">Acreage & ag work is our western specialty:</strong> ranch gates bent by stock, cattle guards, pipe fence corners, horse and stock trailers, and implement repairs — we bring everything needed to work off your pasture, no power required.
                </p>
              </div>
            </Reveal>
          )}
          <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl mb-8" data-testid="city-services-heading">
            Repairs we handle in {city.name}
          </h2>
          <div className="grid sm:grid-cols-2 gap-px bg-warmline border border-warmline" data-testid="city-services-grid">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 2) * 0.06}>
                <Link to={`/service-areas/${city.slug}/${s.slug}`} data-testid={`city-service-${s.slug}`} className="group flex items-center justify-between gap-4 bg-cream hover:bg-sand transition-colors duration-200 p-6 h-full">
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight group-hover:text-rust transition-colors">{s.name} in {city.name}</h3>
                    <p className="mt-1.5 text-sm text-smoke leading-relaxed">{s.blurb}</p>
                  </div>
                  <ArrowRight size={18} className="text-rust shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-12">
            <h2 className="font-display font-extrabold tracking-tight text-xl mb-5">Also serving nearby</h2>
            <div className="flex flex-wrap gap-2.5" data-testid="city-nearby">
              {nearby.map((c) => (
                <Link key={c.slug} to={`/service-areas/${c.slug}`} data-testid={`nearby-${c.slug}`}
                  className="btn-cut border-2 border-ink/60 px-4 py-2 text-sm font-bold hover:bg-charcoal hover:text-cream hover:border-charcoal transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CityDetail;
